/**
 * Hook for loading and filtering organization data
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import type { Organization, FilterState } from '../types';

/**
 * Simple fuzzy search - checks if query terms appear in the text (case-insensitive)
 * Supports partial word matching
 * Returns false if text is undefined/empty, true only if text contains all query words
 */
function fuzzyMatch(text: string | undefined, query: string): boolean {
  // If no text to search in, no match
  if (!text) return false;
  // If no query, consider it a match (everything matches empty query)
  if (!query.trim()) return true;

  const normalizedText = text.toLowerCase();
  const normalizedQuery = query.toLowerCase().trim();

  // Split query into words and check if all appear in text
  const queryWords = normalizedQuery.split(/\s+/).filter(Boolean);
  return queryWords.every(word => normalizedText.includes(word));
}

/**
 * Check if organization matches search query
 * Searches in: name, description, services
 */
function matchesSearch(org: Organization, query: string): boolean {
  if (!query.trim()) return true;

  // Check name
  if (fuzzyMatch(org.name, query)) return true;

  // Check description
  if (fuzzyMatch(org.description, query)) return true;

  // Check og_description
  if (fuzzyMatch(org.og_description, query)) return true;

  // Check services
  if (org.services?.some(service => fuzzyMatch(service, query))) return true;

  // Check city
  if (fuzzyMatch(org.city, query)) return true;

  return false;
}

export function useOrganizations() {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [filters, setFilters] = useState<FilterState>({
    search: '',
    services: [],
    sector: null,
    province: null,
  });

  // Load organizations from static JSON
  // Only include approved organizations (defense-in-depth: build process should only include approved,
  // but we filter client-side as well to ensure pending organizations never appear on the map)
  useEffect(() => {
    async function loadOrganizations() {
      try {
        const response = await fetch('/data/organizations.json');
        if (!response.ok) throw new Error('Failed to load organizations');
        const data: Organization[] = await response.json();
        // Filter to only show approved organizations
        const approvedOrgs = data.filter(org => org.status === 'approved');
        setOrganizations(approvedOrgs);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Unknown error'));
      } finally {
        setLoading(false);
      }
    }

    loadOrganizations();
  }, []);

  // Filter organizations based on current filter state
  const filteredOrganizations = useMemo(() => {
    let result = organizations;

    // Search filter (fuzzy match on name, description, services)
    if (filters.search.trim()) {
      result = result.filter(org => matchesSearch(org, filters.search));
    }

    // Service tags filter (OR logic - org matches if it has ANY of the selected services)
    if (filters.services.length > 0) {
      result = result.filter(org =>
        org.services?.some(service =>
          filters.services.some(filterService =>
            service.toLowerCase().includes(filterService.toLowerCase())
          )
        )
      );
    }

    // Sector filter
    if (filters.sector) {
      result = result.filter(org => org.sector === filters.sector);
    }

    // Province filter
    if (filters.province) {
      result = result.filter(org => org.province === filters.province);
    }

    return result;
  }, [organizations, filters]);

  // Convenience methods for updating individual filter fields
  const updateSearch = useCallback((search: string) => {
    setFilters(prev => ({ ...prev, search }));
  }, []);

  const updateServices = useCallback((services: string[]) => {
    setFilters(prev => ({ ...prev, services }));
  }, []);

  const updateSector = useCallback((sector: string | null) => {
    setFilters(prev => ({ ...prev, sector }));
  }, []);

  const updateProvince = useCallback((province: string | null) => {
    setFilters(prev => ({ ...prev, province }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({
      search: '',
      services: [],
      sector: null,
      province: null,
    });
  }, []);

  return {
    organizations: filteredOrganizations,
    allOrganizations: organizations,
    loading,
    error,
    filters,
    setFilters,
    // Convenience methods
    updateSearch,
    updateServices,
    updateSector,
    updateProvince,
    clearFilters,
    // Result count for UI
    resultCount: filteredOrganizations.length,
    totalCount: organizations.length,
  };
}
